import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../../utils/api';
import type {
  ClassificationFilter,
  InstanceTypeResponse,
  CreateInstanceTypeParams,
  LayerFunctionOption,
  Layer,
  CreateLayerParams,
  UpdateLayerParams,
  DeleteLayerParams,
} from '../types';

export type {
  ClassificationFilter,
  ViewMode,
  InstanceTypeCreateRequest,
  InstanceTypeResponse,
  CreateInstanceTypeParams,
  LayerFunctionOption,
  LayerFunction,
  MaterialPattern,
  ClassifiedSolution,
  Layer,
  LayerRequest,
  LayerPatchRequest,
  CreateLayerParams,
  UpdateLayerParams,
  DeleteLayerParams,
} from '../types';

// ============================================
// GET /api/v1/menu/{id}/classification-filters/
// ============================================

export const useGetClassificationFilters = (menuId: string | null | undefined) => {
  return useQuery({
    queryKey: ['classification-filters', menuId],
    queryFn: async () => {
      const { data } = await apiService.get<ClassificationFilter[]>(
        `/api/v1/menu/${menuId}/classification-filters/`
      );
      return data;
    },
    enabled: !!menuId,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });
};

// ============================================
// POST /api/v1/instance-type/
// ============================================

/**
 * Hook to create a new instance type
 */
export const useCreateInstanceType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: CreateInstanceTypeParams) => {
      const response = await apiService.post<InstanceTypeResponse>('/api/v1/instance-type/', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate instance type queries to refetch
      queryClient.invalidateQueries(['instance-type']);
    },
  });
};

// ============================================
// GET /api/v1/instance-type/layer/functions/
// ============================================

/**
 * Hook to fetch available layer functions
 */
export const useGetLayerFunctions = () => {
  return useQuery({
    queryKey: ['layer-functions'],
    queryFn: async () => {
      const { data } = await apiService.get<LayerFunctionOption[]>(
        '/api/v1/instance-type/layer/functions/'
      );
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

// ============================================
// GET /api/v1/instance-type/{id}/layers/
// ============================================

/**
 * Hook to fetch all layers for an instance type
 */
export const useGetInstanceTypeLayers = (instanceTypeId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['instance-type-layers', instanceTypeId],
    queryFn: async () => {
      const { data } = await apiService.get<Layer[]>(
        `/api/v1/instance-type/${instanceTypeId}/layers/`
      );
      return data;
    },
    enabled: enabled && !!instanceTypeId,
    refetchOnWindowFocus: false,
  });
};

// ============================================
// POST /api/v1/instance-type/{id}/layers/
// ============================================

/**
 * Hook to create a new layer for an instance type
 */
export const useCreateInstanceTypeLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceTypeId, data }: CreateLayerParams) => {
      const response = await apiService.post<Layer>(
        `/api/v1/instance-type/${instanceTypeId}/layers/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate layers list to refetch
      queryClient.invalidateQueries(['instance-type-layers', variables.instanceTypeId]);
    },
  });
};

// ============================================
// GET /api/v1/instance-type/{id}/layers/{layer_id}/
// ============================================

/**
 * Hook to fetch a single layer by ID
 */
export const useGetInstanceTypeLayer = (
  instanceTypeId: string,
  layerId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['instance-type-layer', instanceTypeId, layerId],
    queryFn: async () => {
      const { data } = await apiService.get<Layer>(
        `/api/v1/instance-type/${instanceTypeId}/layers/${layerId}/`
      );
      return data;
    },
    enabled: enabled && !!instanceTypeId && !!layerId,
    refetchOnWindowFocus: false,
  });
};

// ============================================
// PATCH /api/v1/instance-type/{id}/layers/{layer_id}/
// ============================================

/**
 * Hook to update an existing layer
 */
export const useUpdateInstanceTypeLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceTypeId, layerId, data }: UpdateLayerParams) => {
      const response = await apiService.patch<Layer>(
        `/api/v1/instance-type/${instanceTypeId}/layers/${layerId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate both the single layer and the list
      queryClient.invalidateQueries([
        'instance-type-layer',
        variables.instanceTypeId,
        variables.layerId,
      ]);
      queryClient.invalidateQueries(['instance-type-layers', variables.instanceTypeId]);
    },
  });
};

// ============================================
// DELETE /api/v1/instance-type/{id}/layers/{layer_id}/
// ============================================

/**
 * Hook to delete a layer
 */
// ============================================
// POST /api/v1/instance-type/update-status/
// ============================================

export const useUpdateInstanceTypeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instanceTypeIds,
      status,
    }: {
      instanceTypeIds: string[];
      status: string;
    }) => {
      const response = await apiService.patch('/api/v1/instance-type/update-status/', {
        instance_type_ids: instanceTypeIds,
        status,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['instance-type']);
    },
  });
};

export const useDeleteInstanceTypeLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceTypeId, layerId }: DeleteLayerParams) => {
      await apiService.delete(`/api/v1/instance-type/${instanceTypeId}/layers/${layerId}/`);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the single layer and the list
      queryClient.invalidateQueries([
        'instance-type-layer',
        variables.instanceTypeId,
        variables.layerId,
      ]);
      queryClient.invalidateQueries(['instance-type-layers', variables.instanceTypeId]);
    },
  });
};
